import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Link,
    List,
    ListItem,
    ListItemText,
    Typography,
    useTheme
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import ApiClient from '../../config/ApiClient';
import { LessonPlans, LessonRecording } from '../../object-actions/types/types';

interface LessonRecordingsModalProps {
    open: boolean;
    onClose: () => void;
    coachContentId: number;
}

const LessonRecordingsModal: React.FC<LessonRecordingsModalProps> = ({
    open,
    onClose,
    coachContentId
}) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [lessonPlan, setLessonPlan] = useState<LessonPlans | null>(null);
    const [recordings, setRecordings] = useState<LessonRecording[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && coachContentId) {
            fetchData();
        } else {
            // Reset state when modal closes
            setLessonPlan(null);
            setRecordings([]);
            setError(null);
        }
    }, [open, coachContentId]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Fetch LessonPlans instance for this CoachContent
            const lessonPlanResponse = await ApiClient.get<LessonPlans>(
                `/api/lesson-plans/by_coach_content/?coach_content=${coachContentId}`
            );

            if (!lessonPlanResponse.success || !lessonPlanResponse.data) {
                setError('Failed to load lesson plan');
                setLoading(false);
                return;
            }

            const lessonPlanData = lessonPlanResponse.data;
            setLessonPlan(lessonPlanData);

            // Fetch recordings for this lesson plan
            const lessonPlanId = typeof lessonPlanData.id === 'number'
                ? lessonPlanData.id
                : Number(lessonPlanData.id);

            const recordingsResponse = await ApiClient.get<LessonRecording[]>(
                `/api/lesson-plans/${lessonPlanId}/recordings/`
            );

            if (recordingsResponse.success && recordingsResponse.data) {
                setRecordings(Array.isArray(recordingsResponse.data) ? recordingsResponse.data : []);
            } else {
                setRecordings([]);
            }
        } catch (err: any) {
            console.error('Error fetching lesson plan and recordings:', err);
            setError(err?.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const formatDuration = (seconds: number | null | undefined): string => {
        if (!seconds) return 'N/A';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatFileSize = (mb: number | null | undefined): string => {
        if (!mb) return 'N/A';
        return `${mb.toFixed(2)} MB`;
    };

    const formatDate = (dateString: string | null | undefined): string => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString();
        } catch {
            return 'N/A';
        }
    };

    const isVideo = (mimeType: string | null | undefined): boolean => {
        if (!mimeType) return false;
        return mimeType.startsWith('video/');
    };

    const isAudio = (mimeType: string | null | undefined): boolean => {
        if (!mimeType) return false;
        return mimeType.startsWith('audio/');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle>
                <Typography variant="h6" component="div">
                    Lesson Plan Recordings
                </Typography>
            </DialogTitle>
            <DialogContent dividers sx={{ overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 2 }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : (
                    <Box>
                        {lessonPlan && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Lesson Plan Details
                                </Typography>
                                {lessonPlan.notes && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {lessonPlan.notes}
                                    </Typography>
                                )}
                                {lessonPlan.marked_complete_at && (
                                    <Typography variant="body2" color="text.secondary">
                                        Completed: {formatDate(lessonPlan.marked_complete_at)}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                            Recordings ({recordings.length})
                        </Typography>

                        {recordings.length === 0 ? (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    No recordings found for this lesson plan.
                                </Typography>
                            </Box>
                        ) : (
                            <List>
                                {recordings.map((recording, index) => (
                                    <React.Fragment key={recording.id || index}>
                                        <ListItem
                                            sx={{
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                bgcolor: theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.02)'
                                                    : 'rgba(0, 0, 0, 0.01)',
                                                borderRadius: 1,
                                                mb: 1,
                                                p: 2
                                            }}
                                        >
                                            <Box sx={{ width: '100%' }}>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                                                            Recording {index + 1}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Box sx={{ mt: 1 }}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Created: {formatDate(recording.created_at)}
                                                            </Typography>
                                                            {recording.duration && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Duration: {formatDuration(recording.duration)}
                                                                </Typography>
                                                            )}
                                                            {recording.file_size && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Size: {formatFileSize(recording.file_size)}
                                                                </Typography>
                                                            )}
                                                            {recording.mime_type && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Type: {recording.mime_type}
                                                                </Typography>
                                                            )}
                                                            {recording.notes && (
                                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                                    Notes: {recording.notes}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                                {recording.recording && (
                                                    <Box sx={{ mt: 2, width: '100%' }}>
                                                        {isVideo(recording.mime_type) ? (
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    maxWidth: '100%',
                                                                    borderRadius: 1,
                                                                    overflow: 'hidden',
                                                                    backgroundColor: theme.palette.mode === 'dark'
                                                                        ? 'rgba(0, 0, 0, 0.3)'
                                                                        : 'rgba(0, 0, 0, 0.05)'
                                                                }}
                                                            >
                                                                <video
                                                                    controls
                                                                    src={recording.recording}
                                                                    style={{
                                                                        width: '100%',
                                                                        display: 'block'
                                                                    }}
                                                                />
                                                            </Box>
                                                        ) : isAudio(recording.mime_type) ? (
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    p: 1,
                                                                    borderRadius: 1,
                                                                    backgroundColor: theme.palette.mode === 'dark'
                                                                        ? 'rgba(0, 0, 0, 0.3)'
                                                                        : 'rgba(0, 0, 0, 0.05)'
                                                                }}
                                                            >
                                                                <audio
                                                                    controls
                                                                    src={recording.recording}
                                                                    style={{
                                                                        width: '100%'
                                                                    }}
                                                                />
                                                            </Box>
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    maxWidth: '100%',
                                                                    borderRadius: 1,
                                                                    overflow: 'hidden',
                                                                    backgroundColor: theme.palette.mode === 'dark'
                                                                        ? 'rgba(0, 0, 0, 0.3)'
                                                                        : 'rgba(0, 0, 0, 0.05)'
                                                                }}
                                                            >
                                                                <video
                                                                    controls
                                                                    src={recording.recording}
                                                                    style={{
                                                                        width: '100%',
                                                                        display: 'block'
                                                                    }}
                                                                />
                                                            </Box>
                                                        )}
                                                        <Box sx={{ mt: 1 }}>
                                                            <Link
                                                                href={recording.recording}
                                                                download={recording.recording.split('/').pop() || 'recording'}
                                                                underline="hover"
                                                                variant="body2"
                                                            >
                                                                Download recording
                                                            </Link>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        </ListItem>
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LessonRecordingsModal;

